import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractFieldErrors } from '@/shared/api/errors';
import { apiAddressPost } from '@/entities/addresses';
import { FeaturesAddressesAddressForm } from '@/features/addresses';
import {
  addressFormSchema,
  emptyAddressFormValues,
  mapFormValuesToPayload,
  type AddressFormValues,
} from '@/features/addresses/modules/types';
import type { ApiAddressesPostBody } from '@/shared/types/api/addresses';

const FORM_ID = 'address-create-form';
const BACK_TO = '/settings/addresses';

export const WidgetsAddressCreateMain = () => {
  const { t } = useTranslation('addresses');
  const navigate = useNavigate();
  const toast = useToast();

  const form = useForm<AddressFormValues>({
    resolver: zodResolver(addressFormSchema),
    defaultValues: emptyAddressFormValues,
    mode: 'onBlur',
  });

  const mutation = useMutate({
    api: (payload: ApiAddressesPostBody) => apiAddressPost(payload),
    invalidateKeys: [queryKeys.addresses.all],
    onSuccess: () => {
      toast.success(t('toast.created'));
      navigate(BACK_TO);
    },
    onError: (err, message) => {
      const fieldErrors = extractFieldErrors(err);
      Object.entries(fieldErrors).forEach(([field, msg]) => {
        if (field in emptyAddressFormValues) {
          form.setError(field as keyof AddressFormValues, { type: 'server', message: msg });
        }
      });
      toast.error(message);
    },
  });

  const onSubmit = (values: AddressFormValues): void => {
    mutation.mutate(mapFormValuesToPayload(values));
  };

  return (
    <>
      <PageHeader title={t('pageTitle.create')} showBack backTo={BACK_TO} />
      <div className="flex flex-col gap-4 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        <FeaturesAddressesAddressForm id={FORM_ID} form={form} onSubmit={onSubmit} />
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(BACK_TO)}
            disabled={mutation.isPending}
            block
          >
            {t('common:cancel')}
          </Button>
          <Button type="submit" form={FORM_ID} loading={mutation.isPending} block>
            {t('common:save')}
          </Button>
        </div>
      </div>
    </>
  );
};
